import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import type { Editor } from '@tiptap/core';
import {
  getCharacterCueDialogueDecorations,
  getSceneDialogueStats,
  isSceneHeadingParagraphText,
  SCENE_HEADING_DOM_CLASS,
} from '@narrativebench/editor-core';
import { useLocale } from '../contexts/LocaleContext';
import { findScrollContainer, getMountedEditorDom } from '../utils/editor-utils';
import { formatSceneDialogueStatLabels } from '../utils/script-scene-stats';
import { ScriptInfoIconWithTooltip } from './script-info/ScriptInfoIconWithTooltip';
import type { ScriptInfoStatsLabels } from './script-info/ScriptInfoStats';

export const SCENE_HEADING_SCRIPT_INFO_CLASS = 'scene-heading-script-info';
export const SCENE_HEADING_SCRIPT_INFO_ENTER_EVENT = 'narrativebench:scene-heading-script-info-enter';

export function isPointerOverSceneHeadingScriptInfo(
  clientX: number,
  clientY: number,
  target?: EventTarget | null,
): boolean {
  if (target instanceof Element && target.closest(`.${SCENE_HEADING_SCRIPT_INFO_CLASS}`)) {
    return true;
  }
  if (typeof document.elementFromPoint !== 'function') {
    return false;
  }
  const element = document.elementFromPoint(clientX, clientY);
  return Boolean(element?.closest(`.${SCENE_HEADING_SCRIPT_INFO_CLASS}`));
}

type SceneHeadingIcon = {
  pos: number;
  top: number;
  left: number;
  labels: ScriptInfoStatsLabels;
};

const ICON_INSET_PX = 2;
const ICON_TOP_OFFSET_PX = 2;

export function resolveSceneIconPortalHost(editor: Editor): HTMLElement | null {
  const editorDom = getMountedEditorDom(editor);
  if (!editorDom) {
    return null;
  }
  const scrollContainer = findScrollContainer(editorDom);
  if (scrollContainer) {
    return scrollContainer;
  }
  return editorDom.parentElement instanceof HTMLElement ? editorDom.parentElement : editorDom;
}

function positionRelativeToHost(
  host: HTMLElement,
  rect: DOMRect,
): { top: number; left: number } {
  const hostRect = host.getBoundingClientRect();
  return {
    top: rect.top - hostRect.top + host.scrollTop + ICON_TOP_OFFSET_PX,
    left: rect.left - hostRect.left + host.scrollLeft + ICON_INSET_PX,
  };
}

function collectSceneHeadingIcons(
  editor: Editor,
  t: (key: string) => string,
  host: HTMLElement,
): SceneHeadingIcon[] {
  const icons: SceneHeadingIcon[] = [];
  const decorations = getCharacterCueDialogueDecorations(editor.state) ?? undefined;
  editor.state.doc.descendants((node, pos) => {
    if (node.type.name !== 'paragraph' || !isSceneHeadingParagraphText(node.textContent)) {
      return;
    }
    let dom: HTMLElement | null = null;
    try {
      const nodeDom = editor.view.nodeDOM(pos);
      dom = nodeDom instanceof HTMLElement ? nodeDom : null;
    } catch {
      return;
    }
    if (!dom || !dom.classList.contains(SCENE_HEADING_DOM_CLASS)) {
      return;
    }
    const stats = getSceneDialogueStats(editor.state.doc, pos, decorations);
    if (!stats) {
      return;
    }
    const { top, left } = positionRelativeToHost(host, dom.getBoundingClientRect());
    icons.push({
      pos,
      top,
      left,
      labels: formatSceneDialogueStatLabels(stats, t),
    });
  });
  return icons;
}

export const SceneHeadingScriptInfo: React.FC<{ editor: Editor | null }> = ({ editor }) => {
  const { t } = useLocale();
  const [icons, setIcons] = useState<SceneHeadingIcon[]>([]);
  const [portalHost, setPortalHost] = useState<HTMLElement | null>(null);

  useEffect(() => {
    if (!editor || editor.isDestroyed) {
      setIcons([]);
      setPortalHost(null);
      return;
    }

    let frameId: number | null = null;
    let statsTimeoutId: ReturnType<typeof setTimeout> | null = null;
    let cachedLabelsByPos = new Map<number, ScriptInfoStatsLabels>();

    const refreshPositions = (): void => {
      if (frameId != null) {
        window.cancelAnimationFrame(frameId);
      }
      frameId = window.requestAnimationFrame(() => {
        frameId = null;
        if (!editor || editor.isDestroyed) {
          setIcons([]);
          setPortalHost(null);
          return;
        }
        try {
          const host = resolveSceneIconPortalHost(editor);
          setPortalHost(host);
          if (!host) {
            setIcons([]);
            return;
          }
          if (cachedLabelsByPos.size === 0) {
            setIcons(collectSceneHeadingIcons(editor, t, host));
            return;
          }
          const next: SceneHeadingIcon[] = [];
          editor.state.doc.descendants((node, pos) => {
            if (node.type.name !== 'paragraph' || !isSceneHeadingParagraphText(node.textContent)) {
              return;
            }
            const labels = cachedLabelsByPos.get(pos);
            if (!labels) {
              return;
            }
            let dom: HTMLElement | null = null;
            try {
              const nodeDom = editor.view.nodeDOM(pos);
              dom = nodeDom instanceof HTMLElement ? nodeDom : null;
            } catch {
              return;
            }
            if (!dom || !dom.classList.contains(SCENE_HEADING_DOM_CLASS)) {
              return;
            }
            const { top, left } = positionRelativeToHost(host, dom.getBoundingClientRect());
            next.push({
              pos,
              top,
              left,
              labels,
            });
          });
          setIcons(next);
        } catch {
          setIcons([]);
          setPortalHost(null);
        }
      });
    };

    const refreshStatsAndPositions = (): void => {
      if (!editor || editor.isDestroyed) {
        setIcons([]);
        setPortalHost(null);
        cachedLabelsByPos = new Map();
        return;
      }
      try {
        const host = resolveSceneIconPortalHost(editor);
        setPortalHost(host);
        if (!host) {
          cachedLabelsByPos = new Map();
          setIcons([]);
          return;
        }
        const nextIcons = collectSceneHeadingIcons(editor, t, host);
        cachedLabelsByPos = new Map(nextIcons.map((icon) => [icon.pos, icon.labels]));
        setIcons(nextIcons);
      } catch {
        cachedLabelsByPos = new Map();
        setIcons([]);
        setPortalHost(null);
      }
    };

    const scheduleStatsRefresh = (): void => {
      if (statsTimeoutId != null) {
        clearTimeout(statsTimeoutId);
      }
      statsTimeoutId = setTimeout(() => {
        statsTimeoutId = null;
        refreshStatsAndPositions();
      }, 250);
      refreshPositions();
    };

    refreshStatsAndPositions();
    editor.on('transaction', scheduleStatsRefresh);
    window.addEventListener('resize', refreshPositions);

    return () => {
      if (frameId != null) {
        window.cancelAnimationFrame(frameId);
      }
      if (statsTimeoutId != null) {
        clearTimeout(statsTimeoutId);
      }
      editor.off('transaction', scheduleStatsRefresh);
      window.removeEventListener('resize', refreshPositions);
    };
  }, [editor, t]);

  if (!editor || !portalHost || icons.length === 0) {
    return null;
  }

  return createPortal(
    <>
      {icons.map((icon) => (
        <ScriptInfoIconWithTooltip
          key={icon.pos}
          className={SCENE_HEADING_SCRIPT_INFO_CLASS}
          style={{ top: icon.top, left: icon.left }}
          labels={icon.labels}
          onMouseEnter={() => {
            window.dispatchEvent(new Event(SCENE_HEADING_SCRIPT_INFO_ENTER_EVENT));
          }}
        />
      ))}
    </>,
    portalHost,
  );
};
